import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  CITIES,
  EVENT_FORMATS,
  type City,
  type EventFormat,
} from "@nifgash/shared/types";
import { userEventSchema, type UserEventInput } from "@nifgash/shared/validators";
import { AddressAutocomplete, type Picked } from "../components/AddressAutocomplete";
import { Button } from "../components/Button";
import { Chip } from "../components/Chip";
import { CitySelect } from "../components/CitySelect";
import { ErrorBanner } from "../components/ErrorBanner";
import { Input } from "../components/Input";
import { Loader } from "../components/Loader";
import { LocationPicker } from "../components/LocationPicker";
import { MascotPensive } from "../components/Mascot";
import { tg } from "../lib/telegram";
import { uploadEventImage } from "../api/events";
import { getProfile } from "../api/profile";
import { getEligibility, notifyReview, submitEvent } from "../api/user-events";

export function CreateEvent() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [eligible, setEligible] = useState<boolean | null>(null);
  const [reason, setReason] = useState<string>("");
  const [defaultCity, setDefaultCity] = useState<City>("haifa");

  useEffect(() => {
    Promise.all([getEligibility(), getProfile()])
      .then(([elig, profile]) => {
        setEligible(elig.ok);
        if (!elig.ok && "reason" in elig) setReason(elig.reason);
        const c = profile.user.city;
        if (c && (CITIES as readonly string[]).includes(c)) setDefaultCity(c as City);
      })
      .catch(() => setEligible(false));
  }, []);

  useEffect(() => {
    const app = tg();
    app?.MainButton.hide();
    app?.BackButton.show();
    const onBack = () => navigate(-1);
    app?.BackButton.onClick(onBack);
    return () => {
      app?.BackButton.offClick(onBack);
      app?.BackButton.hide();
    };
  }, [navigate]);

  if (eligible === null) return <Loader text={t("createEvent.checkingAccess")} full />;

  if (!eligible) {
    return (
      <div className="min-h-screen bg-surface-soft px-6 pt-12 pb-12 text-center">
        <MascotPensive size={140} />
        <h2 className="text-[22px] font-bold text-ink-900 mt-6">
          {reason === "not_premium" ? t("createEvent.needPremium") : t("createEvent.needFirstVisit")}
        </h2>
        <p className="text-[14px] text-ink-500 mt-3 max-w-[320px] mx-auto leading-relaxed">
          {reason === "not_premium" ? t("createEvent.needPremiumDesc") : t("createEvent.needFirstVisitDesc")}
        </p>
        <div className="mt-6 max-w-[300px] mx-auto">
          {reason === "not_premium" ? (
            <Button stretched size="lg" onClick={() => navigate("/premium")}>
              {t("createEvent.openPremium")}
            </Button>
          ) : (
            <Button stretched size="lg" onClick={() => navigate("/events")}>
              {t("createEvent.toEventsList")}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return <CreateEventForm defaultCity={defaultCity} navigate={navigate} />;
}

function CreateEventForm({
  defaultCity,
  navigate,
}: {
  defaultCity: City;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [format, setFormat] = useState<EventFormat | null>(null);
  const [customFormat, setCustomFormat] = useState("");
  const [venueQuery, setVenueQuery] = useState("");
  const [picked, setPicked] = useState<Picked | null>(null);
  const [city, setCity] = useState<City>(defaultCity);
  const [startsAtLocal, setStartsAtLocal] = useState("");
  const [duration, setDuration] = useState(90);
  const [capacity, setCapacity] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);

  async function submit() {
    setError(null);
    if (!format) return setError(t("createEvent.pickFormat"));
    // "Other" must be filled in with a custom label.
    const effectiveFormat = format === "other" ? customFormat.trim() : format;
    if (format === "other" && !effectiveFormat) return setError(t("createEvent.pickCustomFormat"));
    if (!picked) return setError(t("createEvent.pickPlaceFromHints"));

    const startsAtIso = startsAtLocal ? new Date(startsAtLocal).toISOString() : "";

    const candidate: UserEventInput = {
      title: title.trim(),
      description: description.trim() || undefined,
      // Prefer whatever is in the field (which holds the detailed address
      // after a map pick) so the card shows the full location, not a stub.
      venue_name: venueQuery.trim() || picked.venue_name.trim(),
      venue_address: picked.address.trim() || undefined,
      city,
      format: effectiveFormat,
      starts_at: startsAtIso,
      duration_minutes: duration,
      capacity: capacity ? Number(capacity) : undefined,
      lat: picked.lat,
      lng: picked.lng,
    };
    const parsed = userEventSchema.safeParse(candidate);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? t("createEvent.fillRequired"));
      tg()?.HapticFeedback?.impactOccurred("medium");
      return;
    }

    setSubmitting(true);
    try {
      const { event_id } = await submitEvent(parsed.data);
      // Cover image is optional and best-effort: a failed upload shouldn't
      // block the (already created) event from being submitted for review.
      if (image) await uploadEventImage(event_id, image).catch(() => {});
      // Notify admins LAST so the moderation message includes the photo.
      await notifyReview(event_id).catch(() => {});
      tg()?.HapticFeedback?.impactOccurred("medium");
      tg()?.showAlert(t("createEvent.successSubmitted"));
      navigate("/events");
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg.includes("403") ? t("createEvent.noPermission") : t("createEvent.submitFailed", { msg }));
      tg()?.HapticFeedback?.impactOccurred("heavy");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-surface-soft pb-32">
      <div className="px-5 pt-8 pb-6 bg-surface border-b border-ink-100">
        <h1 className="text-[26px] font-extrabold text-ink-900 leading-tight tracking-tight">
          {t("createEvent.title")}
        </h1>
        <p className="text-[13px] text-ink-500 mt-1">
          {t("createEvent.subtitle")}
        </p>
      </div>

      {error && <ErrorBanner message={error} />}

      <div className="px-5 mt-5 space-y-5">
        <FieldGroup label={t("createEvent.fields.name")}>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("createEvent.fields.namePlaceholder")}
          />
        </FieldGroup>

        <FieldGroup label={t("createEvent.fields.format")}>
          <div className="flex flex-wrap gap-2">
            {EVENT_FORMATS.map((f) => (
              <Chip
                key={f}
                size="md"
                selected={format === f}
                onClick={() => setFormat(f)}
              >
                {t(`labels.format.${f}`)}
              </Chip>
            ))}
          </div>
          {format === "other" && (
            <div className="mt-2">
              <Input
                value={customFormat}
                autoFocus
                maxLength={40}
                onChange={(e) => setCustomFormat(e.target.value)}
                placeholder={t("createEvent.fields.customFormatPlaceholder")}
              />
            </div>
          )}
        </FieldGroup>

        <FieldGroup label={t("createEvent.fields.where")}>
          <AddressAutocomplete
            value={venueQuery}
            onChange={(q) => {
              setVenueQuery(q);
              if (picked && q !== picked.venue_name) setPicked(null);
            }}
            onPick={(p) => {
              setPicked(p);
              setVenueQuery(p.venue_name);
            }}
            placeholder={t("createEvent.fields.wherePlaceholder")}
          />
          <p className="mt-2 text-[12px] text-ink-500">{t("createEvent.fields.orPickOnMap")}</p>
          <div className="mt-2">
            <LocationPicker
              point={picked ? { lat: picked.lat, lng: picked.lng } : null}
              onPick={(p) => {
                setPicked(p);
                // Show the detailed address (not just the 1-2 word place
                // name) so the user sees a full, Google-Maps-pasteable
                // location. Reverse-geocode may return nothing (a field in
                // the middle of nowhere) — keep the typed text in that case.
                const label = p.address || p.venue_name;
                if (label) setVenueQuery(label);
              }}
            />
          </div>
          {picked && (
            <div className="mt-2 text-[12px] text-ink-500 flex items-start gap-1.5">
              <span className="text-brand-purple flex-shrink-0">📍</span>
              <span className="leading-snug">{picked.display_name || `${picked.lat.toFixed(5)}, ${picked.lng.toFixed(5)}`}</span>
            </div>
          )}
          <p className="mt-1.5 text-[11px] text-ink-400 leading-snug">{t("createEvent.fields.mapHint")}</p>
        </FieldGroup>

        <FieldGroup label={t("createEvent.fields.city")}>
          <CitySelect value={city} onChange={(c) => c && setCity(c)} />
        </FieldGroup>

        <FieldGroup label={t("createEvent.fields.when")}>
          <input
            type="datetime-local"
            value={startsAtLocal}
            onChange={(e) => setStartsAtLocal(e.target.value)}
            className="w-full h-13 px-4 text-base bg-ink-50 border border-ink-200 rounded-[var(--radius-md)] text-ink-900 focus:outline-none focus:border-brand-purple focus:bg-surface focus:ring-4 focus:ring-brand-purple/10 transition-all duration-150"
          />
        </FieldGroup>

        <FieldGroup label={t("createEvent.fields.duration")}>
          <div className="flex flex-wrap gap-2">
            {[60, 90, 120, 180].map((m) => (
              <Chip key={m} size="md" selected={duration === m} onClick={() => setDuration(m)}>
                {t("createEvent.fields.durationMin", { m })}
              </Chip>
            ))}
            {/* "3+ hours" — stores 240 (the schema caps duration at 480),
                covering everything longer than the fixed options. */}
            <Chip size="md" selected={duration > 180} onClick={() => setDuration(240)}>
              {t("createEvent.fields.durationLong")}
            </Chip>
          </div>
        </FieldGroup>

        <FieldGroup label={t("createEvent.fields.capacity")}>
          <Input
            type="number"
            inputMode="numeric"
            value={capacity}
            onChange={(e) => setCapacity(e.target.value)}
            placeholder={t("createEvent.fields.capacityPlaceholder")}
          />
        </FieldGroup>

        <FieldGroup label={t("createEvent.fields.description")}>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t("createEvent.fields.descriptionPlaceholder")}
            rows={4}
            className="w-full px-4 py-3 text-base bg-ink-50 border border-ink-200 rounded-[var(--radius-md)] text-ink-900 placeholder:text-ink-400 focus:outline-none focus:border-brand-purple focus:bg-surface focus:ring-4 focus:ring-brand-purple/10 transition-all duration-150 resize-y"
          />
        </FieldGroup>

        <FieldGroup label={t("createEvent.fields.image")}>
          {imagePreview ? (
            <div className="relative">
              <img
                src={imagePreview}
                alt=""
                className="w-full h-44 object-cover rounded-[var(--radius-md)] border border-ink-100"
              />
              <button
                type="button"
                onClick={() => {
                  setImage(null);
                  setImagePreview(null);
                }}
                className="absolute top-2 end-2 w-8 h-8 rounded-full bg-ink-900/70 text-white flex items-center justify-center text-[18px] leading-none"
                aria-label={t("createEvent.fields.imageRemove")}
              >
                ×
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => imageInputRef.current?.click()}
              className="w-full h-24 rounded-[var(--radius-md)] border-2 border-dashed border-ink-200 text-ink-500 text-[14px] font-semibold flex flex-col items-center justify-center gap-1 hover:border-brand-purple hover:text-brand-purple transition-colors"
            >
              <span className="text-[22px]">🖼️</span>
              {t("createEvent.fields.imageAdd")}
            </button>
          )}
          <input
            ref={imageInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (!f) return;
              if (f.size > 5 * 1024 * 1024) {
                setError(t("createEvent.fields.imageTooLarge"));
                return;
              }
              setImage(f);
              const reader = new FileReader();
              reader.onload = (ev) => setImagePreview(String(ev.target?.result ?? ""));
              reader.readAsDataURL(f);
            }}
          />
        </FieldGroup>
      </div>

      <div
        className="fixed bottom-0 left-0 right-0 px-5 py-4 bg-surface border-t border-ink-100"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)" }}
      >
        <Button
          stretched
          size="lg"
          onClick={() => void submit()}
          disabled={submitting}
        >
          {submitting ? t("createEvent.submitting") : t("createEvent.submitButton")}
        </Button>
      </div>
    </div>
  );
}

function FieldGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[13px] font-semibold text-ink-500 uppercase tracking-wider mb-2">
        {label}
      </div>
      {children}
    </div>
  );
}
